/**
 * Page wizard de creation de personnage - orchestrateur des etapes.
 */

import { loadIndexes } from '@data/indexes';
import type { Peuple, Profil } from '@data/schemas';
import {
  CARACS,
  CARAC_SERIES,
  CARAC_SERIES_META,
  type Carac,
  type CaracSerieId,
} from '@domain/caracs';
import { type PersonnageCreationInput, createPersonnage } from '@domain/personnage';
import { savePersonnage } from '@persistence/personnages';
import {
  type WizardState,
  isStepValid,
  wizardNext,
  wizardPrev,
  wizardReset,
  wizardState,
  wizardUpdate,
} from '@state/creation-wizard';
import { html, nothing } from 'lit';
import { navigate } from '../router';

const STEP_LABELS = [
  'Identite',
  'Profil',
  'Peuple',
  'Caracs',
  'Physique',
  'Voies',
  'Equip.',
] as const;

const IDEAUX = [
  'Abnegation',
  'Clemence',
  'Compassion',
  'Courage',
  'Egalite',
  'Education',
  'Fraternite',
  'Frugalite',
  'Generosite',
  'Honnetete',
  'Honneur',
  'Humilite',
  'Justice',
  'Liberte',
  'Loyaute',
  'Pacifisme',
  'Protection',
  'Sens du sacrifice',
  'Solidarite',
  'Verite',
] as const;

const DEFAUTS = [
  'Alcoolique',
  'Couard',
  'Credule',
  'Cupide',
  'Colerique',
  'Distrait',
  'Dragueur',
  'Fanfaron',
  'Gourmand',
  'Grossier',
  'Impatient',
  'Indecis',
  'Menteur',
  'Orgueilleux',
  'Paranoiaque',
  'Paresseux',
  'Rancunier',
  'Timide',
  'Violent',
  'Voleur',
] as const;

const PHOBIES = [
  'Araignees',
  'Serpents',
  'Hauteur',
  'Eau profonde',
  'Feu',
  'Obscurite',
  'Morts-vivants',
  'Espaces clos',
  'Sang',
  'Foule',
  'Insectes',
  'Orages',
  'Solitude',
  'Magie',
  'Mer/Ocean',
  'Fantomes',
  'Dragons',
  'Foret profonde',
  'Enterrement vivant',
  'Poison',
] as const;

const SECRETS_INTIMES = [
  'Je ne suis pas celui que je pretends etre',
  'Je recherche un membre de ma famille',
  "Je suis victime d'une malediction",
  'Je suis recherche pour un crime',
  "J'ai perdu la memoire d'une periode de ma vie",
  "J'ai une phobie que j'ai honte d'avouer",
  "J'ai une addiction qui me cause du tort",
  'Il y a une chose qui me fait sortir de mes gonds',
  'Un membre de ma famille est devenu un ennemi mortel',
  'Je mene une double vie',
  'Je suis deja mort une fois',
  "J'ai d'enormes dettes",
  'Deux esprits cohabitent dans mon corps',
  "J'ai brise un serment sacre",
  'Je possede un objet qui ne doit pas tomber entre de mauvaises mains',
  'Je porte un deuil terrible',
  "J'ai fui un amant puissant",
  'Un terrible cauchemar me hante chaque nuit',
  "Je n'ai pas reussi a empecher un grand mal",
  "Je fais partie d'une organisation secrete",
  "Toute ma famille est decedee ou m'a renie",
  "On m'a recrute pour surveiller un membre du groupe",
  "On m'a predit que je causerai une catastrophe",
  "Je suis porteur d'une marque de naissance mysterieuse",
  'Mon enfant/frere/amour a disparu sans explication',
  'Je possede un heritage familial auquel je tiens comme a ma vie',
  "J'ai passe un pacte avec un demon ou une entite",
  "J'ai ete trahi et cela a bouleverse ma vie",
  "J'ai un handicap que je surmonte a tout prix",
  'Je suis celebre pour une histoire que je prefere oublier',
  'Je veux devenir celebre pour gagner un coeur',
  'Je veux prouver ma valeur a un parent',
  "J'ai besoin d'argent pour une bonne cause",
  "Mon objectif affiche n'est pas le vrai",
  'Je veux devenir puissant pour abattre un tyran',
  "J'ai fait quelque chose d'horrible et je me rachete",
  'Je sais comment je vais mourir',
  "Je n'ai aucun secret alors je m'en invente",
  'Un jour tous devront ployer le genou devant moi',
  "Je viens d'un autre monde",
] as const;

const TALENTS_SECONDAIRES = [
  "Jouer d'un instrument",
  'Jeux (echecs, cartes, des)',
  'Calligraphie',
  'Cuisine',
  'Chant',
  'Danse',
  'Dessin / Peinture',
  'Sculpture',
  'Couture / Broderie',
  'Herboristerie',
  'Navigation',
  'Dressage',
  'Conteur / Orateur',
  'Forge / Metallurgie',
  'Menuiserie',
  'Peche',
  'Chasse',
  'Escalade',
  'Acrobatie',
  'Prestidigitation',
] as const;

const LANGUES_DISPONIBLES = [
  'Commun',
  'Elfique',
  'Nain',
  'Gnome',
  'Orcique',
  'Halfelin',
  'Sylvain',
  'Infernal',
  'Celestiel',
  "Langue de l'Ancien Empire",
  'Runique',
  'Draconique',
] as const;

const CHOIX_VOIES: Record<
  string,
  { label: string; options: readonly { value: string; desc: string }[] }
> = {
  'voie-du-maitre-d-armes': {
    label: 'Arme de predilection (+1 ATT avec cette categorie)',
    options: [
      { value: 'Epees', desc: 'Epees (longue, courte, batarde, deux mains)' },
      { value: 'Haches', desc: 'Haches (hache, hache a deux mains)' },
      { value: 'Mains nues', desc: 'Mains nues / combat sans arme' },
      { value: 'Masses/Marteaux', desc: 'Masses et marteaux' },
      { value: 'Lances/Epieux', desc: 'Lances, epieux, piques' },
      { value: 'Armes de jet', desc: 'Armes de jet (dague, javelot, hachette)' },
    ],
  },
  'voie-de-l-archer': {
    label: 'Specialisation (definitif, affecte toute la voie)',
    options: [
      { value: 'Arc', desc: 'Arc : +PER aux DM, +1/rang en Init' },
      {
        value: 'Armes de jet',
        desc: 'Voie du Lancer : +FOR aux DM, portee doublee, pas de bonus Init',
      },
    ],
  },
  'voie-du-traqueur': {
    label: 'Bonus famille aventurier',
    options: [
      { value: '+1 PC', desc: '+1 PC (standard aventurier)' },
      { value: '+1 DR', desc: '+1 DR (variante eclaireur, remplace le PC)' },
    ],
  },
};

function getAllTalentOptions(
  state: WizardState,
  idx: Awaited<ReturnType<typeof loadIndexes>>,
): string[] {
  const set = new Set<string>(TALENTS_SECONDAIRES);
  for (const vid of state.voiesChoisies) {
    const voie = idx.voieClasseById.get(vid);
    if (!voie) continue;
    for (const r of voie.rangs) {
      if (!r.effets) continue;
      for (const e of r.effets) {
        if (e.type === 'bonus_test') {
          for (const comp of e.competences ?? []) {
            set.add(comp.charAt(0).toUpperCase() + comp.slice(1));
          }
        }
      }
    }
  }
  return [...set].sort();
}

function randomFrom(list: readonly string[]): string {
  return list[Math.floor(Math.random() * list.length)] ?? '';
}

function formatSign(n: number): string {
  return n >= 0 ? `+${n}` : `${n}`;
}

function renderTraitField(
  label: string,
  values: string[],
  options: readonly string[],
  onAdd: (v: string) => void,
  onRemove: (idx: number) => void,
) {
  return html`
    <div class="form-field">
      <span class="form-label">${label}</span>
      ${
        values.length > 0
          ? html`
        <div class="trait-tags">
          ${values.map(
            (v, i) => html`
            <span class="trait-tag">
              ${v}
              <button class="trait-tag-remove" type="button" @click=${() => onRemove(i)}>x</button>
            </span>
          `,
          )}
        </div>
      `
          : nothing
      }
      <div class="trait-row">
        <select
          class="form-input trait-select"
          @change=${(e: Event) => {
            const sel = e.target as HTMLSelectElement;
            const v = sel.value;
            if (v && v !== '__custom__') {
              onAdd(v);
            }
            sel.value = '';
          }}
        >
          <option value="">-- Ajouter --</option>
          ${options
            .filter((opt) => !values.includes(opt))
            .map((opt) => html`<option value=${opt}>${opt}</option>`)}
        </select>
        <button
          class="btn btn-random"
          type="button"
          title="Aleatoire"
          @click=${() => {
            const available = options.filter((o) => !values.includes(o));
            if (available.length > 0) onAdd(randomFrom(available));
          }}
        >🎲</button>
      </div>
      <input
        type="text"
        class="form-input"
        placeholder="Ou saisir un choix libre puis Entree..."
        style="margin-top: var(--space-1);"
        @keydown=${(e: KeyboardEvent) => {
          if (e.key === 'Enter') {
            const input = e.target as HTMLInputElement;
            const v = input.value.trim();
            if (v && !values.includes(v)) {
              onAdd(v);
              input.value = '';
            }
            e.preventDefault();
          }
        }}
      />
    </div>
  `;
}

export async function renderPersonnageNewPage(): Promise<unknown> {
  const idx = await loadIndexes();
  const state = wizardState.get();

  return html`
    <div class="wizard">
      <h1 class="page-title">Nouveau Personnage</h1>
      ${renderStepper(state)}
      <div class="wizard-content">
        ${renderStep(state, idx)}
      </div>
      ${renderNav(state, idx)}
    </div>
  `;
}

function renderStepper(state: WizardState) {
  return html`
    <div class="wizard-stepper">
      ${STEP_LABELS.map(
        (label, i) => html`
          <span class="wizard-step-dot ${state.step === i + 1 ? 'active' : ''} ${state.step > i + 1 ? 'done' : ''}">
            ${state.step > i + 1 ? '✓' : i + 1}
          </span>
          <span class="wizard-step-label ${state.step === i + 1 ? 'active' : ''}">${label}</span>
          ${i < 6 ? html`<span class="wizard-step-line ${state.step > i + 1 ? 'done' : ''}"></span>` : nothing}
        `,
      )}
    </div>
  `;
}

function renderStep(state: WizardState, idx: Awaited<ReturnType<typeof loadIndexes>>) {
  const profils = idx.bdd.profils.profils;
  const peuples = idx.bdd.peuples.peuples;
  switch (state.step) {
    case 1:
      return renderStepIdentite(state);
    case 2:
      return renderStepProfil(state, profils);
    case 3:
      return renderStepPeuple(state, peuples);
    case 4:
      return renderStepCaracs(state, peuples);
    case 5:
      return renderStepPhysique(state, peuples);
    case 6:
      return renderStepVoies(state, idx);
    case 7:
      return renderStepEquipement(state, profils, peuples, idx);
  }
}

function renderStepIdentite(state: WizardState) {
  return html`
    <div class="wizard-form">
      <label class="form-field">
        <span class="form-label">Nom du personnage *</span>
        <input
          type="text"
          class="form-input"
          id="wizard-nom"
          .value=${state.nom}
          placeholder="Ex: Thorin, Elara, Grok..."
          maxlength="80"
          autofocus
        />
      </label>
      <label class="form-field">
        <span class="form-label">Concept et historique</span>
        <textarea
          class="form-input"
          id="wizard-concept"
          .value=${state.concept}
          placeholder="Qui est votre personnage ? D'ou vient-il ? Qu'a-t-il fait ? Quel evenement l'a pousse a l'aventure ?"
          rows="3"
          style="resize: vertical;"
        ></textarea>
      </label>
      ${renderTraitField(
        'Ideal * (1 ou plus)',
        state.ideaux,
        IDEAUX,
        (v) => {
          syncTextInputs();
          wizardUpdate({ ideaux: [...wizardState.get().ideaux, v] });
        },
        (i) => {
          syncTextInputs();
          wizardUpdate({ ideaux: wizardState.get().ideaux.filter((_, idx) => idx !== i) });
        },
      )}
      ${renderTraitField(
        'Defaut * (1 ou plus)',
        state.defauts,
        DEFAUTS,
        (v) => {
          syncTextInputs();
          wizardUpdate({ defauts: [...wizardState.get().defauts, v] });
        },
        (i) => {
          syncTextInputs();
          wizardUpdate({ defauts: wizardState.get().defauts.filter((_, idx) => idx !== i) });
        },
      )}
      ${renderTraitField(
        'Phobie * (1 ou plus)',
        state.phobies,
        PHOBIES,
        (v) => {
          syncTextInputs();
          wizardUpdate({ phobies: [...wizardState.get().phobies, v] });
        },
        (i) => {
          syncTextInputs();
          wizardUpdate({ phobies: wizardState.get().phobies.filter((_, idx) => idx !== i) });
        },
      )}
      ${renderTraitField(
        'Secret intime * (1 ou plus)',
        state.secretsIntimes,
        SECRETS_INTIMES,
        (v) => {
          syncTextInputs();
          wizardUpdate({ secretsIntimes: [...wizardState.get().secretsIntimes, v] });
        },
        (i) => {
          syncTextInputs();
          wizardUpdate({
            secretsIntimes: wizardState.get().secretsIntimes.filter((_, idx) => idx !== i),
          });
        },
      )}
    </div>
  `;
}

function renderStepProfil(state: WizardState, profils: readonly Profil[]) {
  const families = ['combattants', 'mages', 'mystiques', 'aventuriers'] as const;
  return html`
    <div class="wizard-profils">
      <label class="form-field" style="margin-bottom: var(--space-4);">
        <label style="display: flex; align-items: center; gap: var(--space-2); cursor: pointer;">
          <input type="checkbox" ?checked=${state.isHybride} @change=${(e: Event) => wizardUpdate({ isHybride: (e.target as HTMLInputElement).checked, profilSecondaireId: null, profilPrimaireId: null })} />
          <span>Profil hybride (option avancee, accord MJ requis)</span>
        </label>
      </label>

      <h3>${state.isHybride ? 'Profil 1' : 'Profil'}</h3>
      ${families.map((fam) => {
        const fp = profils.filter((p) => p.famille_id === fam);
        return html`
          <div class="profil-family">
            <h3 class="profil-family-title">${fam}</h3>
            <div class="profil-grid">
              ${fp.map(
                (p) => html`
                  <button
                    class="profil-card ${state.profilId === p.id ? 'selected' : ''}"
                    @click=${() => wizardUpdate({ profilId: p.id })}
                  >
                    <strong>${p.nom}</strong>
                    <span class="muted">${p.caracteristiques_prioritaires.join(', ')}</span>
                    <span class="muted">${p.pv_par_niveau} PV/niv</span>
                  </button>
                `,
              )}
            </div>
          </div>
        `;
      })}

      ${
        state.isHybride
          ? html`
        <h3 style="margin-top: var(--space-5);">Profil 2 (secondaire)</h3>
        <div class="profil-grid">
          ${profils
            .filter((p) => p.id !== state.profilId)
            .map(
              (p) => html`
              <button
                class="profil-card ${state.profilSecondaireId === p.id ? 'selected' : ''}"
                @click=${() => wizardUpdate({ profilSecondaireId: p.id })}
              >
                <strong>${p.nom}</strong>
                <span class="muted">${p.famille_id}</span>
              </button>
            `,
            )}
        </div>

        ${
          state.profilId && state.profilSecondaireId
            ? html`
          <h3 style="margin-top: var(--space-5);">Profil primaire (determine DR, PC, equipement)</h3>
          <div class="profil-grid">
            ${[state.profilId, state.profilSecondaireId].map((pid) => {
              const pr = profils.find((p) => p.id === pid);
              return html`
                <button
                  class="profil-card ${state.profilPrimaireId === pid ? 'selected' : ''}"
                  @click=${() => wizardUpdate({ profilPrimaireId: pid })}
                >
                  <strong>${pr?.nom ?? pid}</strong>
                  <span class="muted">${pr?.famille_id} - ${pr?.pv_par_niveau} PV/niv</span>
                </button>
              `;
            })}
          </div>
        `
            : nothing
        }
      `
          : nothing
      }
    </div>
  `;
}

function renderStepPeuple(state: WizardState, peuples: readonly Peuple[]) {
  return html`
    <div class="wizard-peuples">
      <div class="peuple-grid">
        ${peuples.map(
          (p) => html`
            <button
              class="peuple-card ${state.peupleId === p.id ? 'selected' : ''}"
              @click=${() => wizardUpdate({ peupleId: p.id, peupleChoices: null, voiePeupleId: p.voie_id ?? null })}
            >
              <strong>${p.nom}</strong>
              <span class="muted">${p.taille}</span>
              <div class="peuple-mods">
                ${(p.modificateurs_carac.bonus ?? []).map(
                  (b) =>
                    html`<span class="mod-bonus">+${b.valeur} ${b.carac}${b.alternatif ? ` ou ${b.alternatif}` : ''}</span>`,
                )}
                ${(p.modificateurs_carac.malus ?? []).map(
                  (m) =>
                    html`<span class="mod-malus">${m.valeur} ${m.carac}${m.alternatif ? ` ou ${m.alternatif}` : ''}</span>`,
                )}
              </div>
            </button>
          `,
        )}
      </div>
    </div>
  `;
}

function renderStepCaracs(state: WizardState, peuples: readonly Peuple[]) {
  const series = Object.entries(CARAC_SERIES_META) as [
    CaracSerieId,
    { label: string; hint: string },
  ][];
  const currentSerie = state.caracSerie ? CARAC_SERIES[state.caracSerie] : null;
  const assigned = state.caracAssignment;
  const usedValues = CARACS.map((k) => assigned[k]).filter((v) => v !== 0);
  const peuple = peuples.find((p) => p.id === state.peupleId);
  const bonus = peuple?.modificateurs_carac.bonus ?? [];
  const malus = peuple?.modificateurs_carac.malus ?? [];

  const peupleDeltas: Record<Carac, number> = {
    FOR: 0,
    AGI: 0,
    CON: 0,
    INT: 0,
    PER: 0,
    CHA: 0,
    VOL: 0,
  };
  bonus.forEach((b, i) => {
    if (b.carac === 'libre') {
      if (state.humainBonusCarac && state.humainBonusCarac in peupleDeltas) {
        peupleDeltas[state.humainBonusCarac] += Number(b.valeur);
      }
      return;
    }
    const chosen = state.peupleModBonusChoices[i] ?? b.carac;
    if (chosen && chosen in peupleDeltas) peupleDeltas[chosen as Carac] += Number(b.valeur);
  });
  malus.forEach((m, i) => {
    const chosen = state.peupleModMalusChoices[i] ?? m.carac;
    if (chosen && chosen in peupleDeltas) peupleDeltas[chosen as Carac] += Number(m.valeur);
  });

  return html`
    <div class="wizard-caracs">
      <h3>Choisir une serie</h3>
      <div class="serie-buttons">
        ${series.map(
          ([id, meta]) => html`
            <button
              class="serie-btn ${state.caracSerie === id ? 'selected' : ''}"
              @click=${() => wizardUpdate({ caracSerie: id, caracAssignment: { FOR: 0, AGI: 0, CON: 0, INT: 0, PER: 0, CHA: 0, VOL: 0 } })}
            >
              <strong>${meta.label}</strong>
              <span class="muted">${meta.hint}</span>
              <span class="serie-values">${CARAC_SERIES[id].join(', ')}</span>
            </button>
          `,
        )}
      </div>

      ${
        currentSerie
          ? html`
        <div style="display: flex; align-items: center; gap: var(--space-3); margin-top: var(--space-5);">
          <h3 style="margin: 0;">Distribuer les valeurs</h3>
          <button
            class="btn btn-random"
            type="button"
            title="Repartition aleatoire"
            @click=${() => {
              const shuffled = [...currentSerie].sort(() => Math.random() - 0.5);
              const assignment = Object.fromEntries(
                CARACS.map((k, i) => [k, shuffled[i]]),
              ) as Record<string, number>;
              wizardUpdate({ caracAssignment: assignment as Record<Carac, number> });
            }}
          >🎲</button>
        </div>
        <p class="muted">Choisissez manuellement ou cliquez 🎲 pour une repartition aleatoire.</p>
        <div class="carac-pool">
          ${[...currentSerie]
            .sort((a, b) => b - a)
            .map((val) => {
              const isUsed =
                usedValues.filter((v) => v === val).length >=
                currentSerie.filter((v) => v === val).length;
              return html`<span class="pool-value ${isUsed ? 'used' : ''}">${val >= 0 ? `+${val}` : val}</span>`;
            })}
        </div>
        <div class="carac-assign-grid">
          ${CARACS.map(
            (k) => html`
            <div class="carac-assign-row">
              <span class="carac-name">${k}</span>
              <select
                class="carac-select"
                .value=${String(assigned[k])}
                @change=${(e: Event) => {
                  const val = Number.parseInt((e.target as HTMLSelectElement).value, 10);
                  wizardUpdate({ caracAssignment: { ...assigned, [k]: val } });
                }}
              >
                <option value="0">--</option>
                ${[...new Set(currentSerie)]
                  .sort((a, b) => b - a)
                  .map(
                    (val) =>
                      html`<option value=${val} ?selected=${assigned[k] === val}>${val >= 0 ? `+${val}` : val}</option>`,
                  )}
              </select>
              <span class="carac-final">${assigned[k] >= 0 ? `+${assigned[k]}` : assigned[k]}</span>
            </div>
          `,
          )}
        </div>
      `
          : nothing
      }

      ${
        currentSerie && peuple
          ? html`
        <h3 style="margin-top: var(--space-5);">Modificateurs de peuple (${peuple.nom})</h3>
        <div class="peuple-mod-choices">
          ${bonus.map((b, i) => {
            if (b.carac === 'libre') {
              const minVal = Math.min(...CARACS.map((k) => assigned[k]));
              const weakest = CARACS.filter((k) => assigned[k] === minVal);
              return html`
                <div class="mod-choice-row">
                  <span>+${b.valeur} a une des plus faibles :</span>
                  <select class="form-input" style="width: auto;" @change=${(e: Event) => {
                    wizardUpdate({
                      humainBonusCarac: (e.target as HTMLSelectElement).value as Carac,
                    });
                  }}>
                    <option value="" ?selected=${!state.humainBonusCarac}>-- Choisir --</option>
                    ${weakest.map((k) => html`<option value=${k} ?selected=${state.humainBonusCarac === k}>${k} (${formatSign(assigned[k])})</option>`)}
                  </select>
                </div>
              `;
            }
            if (!b.alternatif) {
              return html`<div class="mod-fixed"><span class="mod-bonus">+${b.valeur} ${b.carac}</span> (fixe)</div>`;
            }
            const chosen = state.peupleModBonusChoices[i] ?? b.carac;
            return html`
              <div class="mod-choice-row">
                <span>Bonus +${b.valeur} :</span>
                <select class="form-input" style="width: auto;" @change=${(e: Event) => {
                  const choices = [...state.peupleModBonusChoices];
                  choices[i] = (e.target as HTMLSelectElement).value;
                  wizardUpdate({ peupleModBonusChoices: choices });
                }}>
                  <option value=${b.carac} ?selected=${chosen === b.carac}>${b.carac}</option>
                  <option value=${b.alternatif} ?selected=${chosen === b.alternatif}>${b.alternatif}</option>
                </select>
              </div>
            `;
          })}
          ${malus.map((m, i) => {
            if (!m.alternatif) {
              return html`<div class="mod-fixed"><span class="mod-malus">${m.valeur} ${m.carac}</span> (fixe)</div>`;
            }
            const chosen = state.peupleModMalusChoices[i] ?? m.carac;
            return html`
              <div class="mod-choice-row">
                <span>Malus ${m.valeur} :</span>
                <select class="form-input" style="width: auto;" @change=${(e: Event) => {
                  const choices = [...state.peupleModMalusChoices];
                  choices[i] = (e.target as HTMLSelectElement).value;
                  wizardUpdate({ peupleModMalusChoices: choices });
                }}>
                  <option value=${m.carac} ?selected=${chosen === m.carac}>${m.carac}</option>
                  <option value=${m.alternatif} ?selected=${chosen === m.alternatif}>${m.alternatif}</option>
                </select>
              </div>
            `;
          })}
        </div>

        <h3 style="margin-top: var(--space-5);">Caracs finales</h3>
        <div class="carac-grid">
          ${CARACS.map((k) => {
            const base = assigned[k];
            const mod = peupleDeltas[k];
            const total = base + mod;
            return html`
              <div class="carac-block">
                <span class="carac-label">${k}</span>
                <span class="carac-value">${total >= 0 ? '+' : ''}${total}</span>
                ${mod !== 0 ? html`<span class="carac-mod ${mod > 0 ? 'mod-bonus' : 'mod-malus'}">${mod > 0 ? '+' : ''}${mod}</span>` : nothing}
              </div>
            `;
          })}
        </div>
      `
          : nothing
      }
    </div>
  `;
}

function parseRange(text: string): { min: number; max: number } | null {
  const nums = text.match(/[\d]+(?:[,.][\d]+)?/g);
  if (!nums || nums.length < 2) return null;
  const a = Number.parseFloat((nums[0] ?? '0').replace(',', '.'));
  const b = Number.parseFloat((nums[1] ?? '0').replace(',', '.'));
  if (text.includes(' m') && !text.includes(' cm')) {
    return { min: Math.round(a * 100), max: Math.round(b * 100) };
  }
  return { min: Math.round(a), max: Math.round(b) };
}

function parseAgeMin(text: string): number {
  const m = text.match(/(\d+)/);
  return m ? Number.parseInt(m[1] ?? '18', 10) : 18;
}

function randBetween(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function renderStepPhysique(state: WizardState, peuples: readonly Peuple[]) {
  const peuple = peuples.find((p) => p.id === state.peupleId);
  const reperes = peuple?.reperes;
  const ageMin = reperes ? parseAgeMin(reperes.age_depart) : 18;
  const ageMax = ageMin + 40;
  const tailleRange = reperes ? parseRange(reperes.taille) : null;
  const poidsRange = reperes ? parseRange(reperes.poids) : null;

  return html`
    <div class="wizard-form">
      <h3>Traits physiques (${peuple?.nom ?? '?'})</h3>
      <p class="muted">Definissez l'apparence physique de votre personnage.</p>
      <div class="form-row" style="grid-template-columns: 1fr 1fr 1fr;">
        <label class="form-field">
          <span class="form-label">Age (${ageMin}-${ageMax} ans)</span>
          <input
            type="number"
            class="form-input"
            .value=${state.age !== null ? String(state.age) : ''}
            @change=${(e: Event) => wizardUpdate({ age: Number.parseInt((e.target as HTMLInputElement).value, 10) || null })}
            min=${ageMin}
            max=${ageMax}
          />
        </label>
        <label class="form-field">
          <span class="form-label">Taille (${tailleRange ? `${tailleRange.min}-${tailleRange.max} cm` : 'cm'})</span>
          <input
            type="number"
            class="form-input"
            .value=${state.taille !== null ? String(state.taille) : ''}
            @change=${(e: Event) => wizardUpdate({ taille: Number.parseInt((e.target as HTMLInputElement).value, 10) || null })}
            min=${tailleRange?.min ?? 80}
            max=${tailleRange?.max ?? 200}
          />
        </label>
        <label class="form-field">
          <span class="form-label">Poids (${poidsRange ? `${poidsRange.min}-${poidsRange.max} kg` : 'kg'})</span>
          <input
            type="number"
            class="form-input"
            .value=${state.poids !== null ? String(state.poids) : ''}
            @change=${(e: Event) => wizardUpdate({ poids: Number.parseInt((e.target as HTMLInputElement).value, 10) || null })}
            min=${poidsRange?.min ?? 20}
            max=${poidsRange?.max ?? 150}
          />
        </label>
      </div>
      <button
        class="btn btn-random"
        type="button"
        style="margin-top: var(--space-3);"
        @click=${() =>
          wizardUpdate({
            age: randBetween(ageMin, ageMax),
            taille: tailleRange
              ? randBetween(tailleRange.min, tailleRange.max)
              : randBetween(150, 180),
            poids: poidsRange ? randBetween(poidsRange.min, poidsRange.max) : randBetween(50, 90),
          })}
      >🎲 Aleatoire</button>
      ${
        reperes
          ? html`
        <div class="muted" style="margin-top: var(--space-3); font-size: var(--font-size-sm);">
          Esperance de vie : ${reperes.esperance_vie}
          ${reperes.traits_physiques ? html`<br/>Traits typiques : ${reperes.traits_physiques}` : nothing}
        </div>
      `
          : nothing
      }
    </div>
  `;
}

function renderStepVoies(state: WizardState, idx: Awaited<ReturnType<typeof loadIndexes>>) {
  const profil = idx.bdd.profils.profils.find((p) => p.id === state.profilId);
  const peuple = idx.bdd.peuples.peuples.find((p) => p.id === state.peupleId);
  if (!profil) return html`<p class="muted">Profil non selectionne.</p>`;

  const profilSecondaire = state.isHybride
    ? idx.bdd.profils.profils.find((p) => p.id === state.profilSecondaireId)
    : null;
  const voiesIds = [
    ...(profil.voies_classe_ids ?? []),
    ...(profilSecondaire?.voies_classe_ids ?? []),
  ];
  const voiesDisponibles = [...new Set(voiesIds)]
    .map((id) => idx.voieClasseById.get(id))
    .filter(Boolean);
  const voieMage =
    profil.voie_du_mage_accessible || profilSecondaire?.voie_du_mage_accessible
      ? idx.voieDuMage
      : null;

  const voiePeupleId = peuple?.voie_id;
  const voiePeuple = voiePeupleId ? idx.voiePeupleById.get(voiePeupleId) : null;
  const isDemiElfe = peuple?.id === 'demi-elfe';
  const voiesPeupleChoix = isDemiElfe
    ? ['voie-de-l-humain', 'voie-de-l-elfe-sylvain', 'voie-de-l-elfe-haut']
        .map((id) => idx.voiePeupleById.get(id))
        .filter(Boolean)
    : [];

  const divinites =
    (
      idx.bdd.pantheon as {
        divinites?: {
          id: string;
          nom: string;
          arme_sacree: string | string[];
          capacite_divine: { label: string; voie_source: string; rang: number };
        }[];
      }
    )?.divinites ?? [];

  return html`
    <div class="wizard-voies">
      ${
        profil?.id === 'pretre'
          ? html`
        <h3>Divinite (Pretre specialiste)</h3>
        <p class="muted">Choisir une divinite determine votre arme sacree et une capacite divine bonus.</p>
        <select
          class="form-input"
          style="margin-bottom: var(--space-3);"
          @change=${(e: Event) => {
            const v = (e.target as HTMLSelectElement).value;
            wizardUpdate({ diviniteId: v || null });
          }}
        >
          <option value="" ?selected=${!state.diviniteId}>-- Generaliste (pas de divinite) --</option>
          ${divinites.map((d) => html`<option value=${d.id} ?selected=${state.diviniteId === d.id}>${d.nom} - Arme: ${Array.isArray(d.arme_sacree) ? d.arme_sacree.join('/') : d.arme_sacree} - ${d.capacite_divine.label} (rang ${d.capacite_divine.rang})</option>`)}
        </select>
        ${
          state.diviniteId
            ? (
                () => {
                  const div = divinites.find((d) => d.id === state.diviniteId);
                  if (!div) return nothing;
                  return html`
            <div class="muted" style="margin-bottom: var(--space-3); padding: var(--space-2); border-radius: var(--radius-sm); background: var(--color-bg-elev-1);">
              <strong>${div.nom}</strong> - Arme sacree : ${Array.isArray(div.arme_sacree) ? div.arme_sacree.join(' ou ') : div.arme_sacree}<br/>
              Capacite divine : <strong>${div.capacite_divine.label}</strong> (${div.capacite_divine.voie_source}, rang ${div.capacite_divine.rang})
              ${div.capacite_divine.rang === 1 ? html`<br/><span style="color: var(--color-accent);">Rang 1 -> acquise obligatoirement a la creation</span>` : html`<br/><span style="color: var(--color-fg-muted);">Rang ${div.capacite_divine.rang} -> acquise en priorite a la montee de niveau</span>`}
            </div>
          `;
                }
              )()
            : nothing
        }
      `
          : nothing
      }

      <h3>Voie de peuple (${peuple?.nom ?? '?'})</h3>
      ${
        voiePeuple
          ? html`
        <div class="voie-grid">
          <div class="voie-card selected voie-peuple">
            <strong>${voiePeuple.nom}</strong>
            <span class="muted voie-rang1">Rang 1 : ${voiePeuple.rangs[0]?.nom ?? ''}</span>
          </div>
        </div>
        <p class="muted">Voie de peuple attribuee automatiquement (rang 1).</p>
      `
          : nothing
      }
      ${
        isDemiElfe
          ? html`
        <p class="muted">Le demi-elfe choisit sa voie de peuple selon son heritage :</p>
        <div class="voie-grid">
          ${voiesPeupleChoix.map((v) => {
            if (!v) return nothing;
            const selected = state.voiePeupleId === v.id;
            return html`
              <button
                class="voie-card voie-peuple ${selected ? 'selected' : ''}"
                @click=${() => wizardUpdate({ voiePeupleId: v.id })}
              >
                <strong>${v.nom}</strong>
                <span class="muted voie-rang1">Rang 1 : ${v.rangs[0]?.nom ?? ''}</span>
              </button>
            `;
          })}
        </div>
      `
          : nothing
      }

      <h3 style="margin-top: var(--space-5);">Voies de classe (2 au choix)</h3>
      <p class="muted">Choisissez 2 voies parmi celles de votre profil et obtenez le rang 1 dans chacune.</p>
      <div class="voie-grid">
        ${voiesDisponibles.map((v) => {
          if (!v) return nothing;
          const selected = state.voiesChoisies.includes(v.id);
          const disabled = !selected && state.voiesChoisies.length >= 2;
          return html`
            <button
              class="voie-card ${selected ? 'selected' : ''}"
              ?disabled=${disabled}
              @click=${() => {
                if (selected) {
                  wizardUpdate({ voiesChoisies: state.voiesChoisies.filter((id) => id !== v.id) });
                } else if (state.voiesChoisies.length < 2) {
                  wizardUpdate({ voiesChoisies: [...state.voiesChoisies, v.id] });
                }
              }}
            >
              <strong>${v.nom}</strong>
              <span class="muted voie-rang1">${v.rangs[0]?.nom ?? 'Rang 1'}</span>
            </button>
          `;
        })}
        ${
          voieMage
            ? html`
          <button
            class="voie-card voie-mage ${state.useVoieMage ? 'selected' : ''}"
            @click=${() => wizardUpdate({ useVoieMage: !state.useVoieMage })}
          >
            <strong>${voieMage.nom}</strong>
            <span class="muted">Voie commune mages</span>
          </button>
        `
            : nothing
        }
      </div>
      <p class="muted" style="margin-top: var(--space-3);">
        ${state.voiesChoisies.length}/2 voies de classe
        ${state.useVoieMage ? ' + Voie du Mage' : ''}
      </p>

      ${
        state.voiesChoisies.some((vid) => CHOIX_VOIES[vid])
          ? html`
        <h3 style="margin-top: var(--space-5);">Choix de specialisation</h3>
        ${state.voiesChoisies
          .filter((vid) => CHOIX_VOIES[vid])
          .map((vid) => {
            const choix = CHOIX_VOIES[vid];
            if (!choix) return nothing;
            const voieNom = idx.voieClasseById.get(vid)?.nom ?? vid;
            return html`
            <div class="form-field" style="margin-bottom: var(--space-2);">
              <span class="form-label">${voieNom} : ${choix.label}</span>
              <select
                class="form-input"
                @change=${(e: Event) => {
                  const v = (e.target as HTMLSelectElement).value;
                  wizardUpdate({ choixVoies: { ...state.choixVoies, [vid]: v } });
                }}
              >
                <option value="" ?selected=${!state.choixVoies[vid]}>-- Choisir --</option>
                ${choix.options.map((opt) => html`<option value=${opt.value} ?selected=${state.choixVoies[vid] === opt.value}>${opt.desc}</option>`)}
              </select>
            </div>
          `;
          })}
      `
          : nothing
      }

      ${
        profil?.famille_id === 'mages' && state.voiesChoisies.length >= 1
          ? html`
        <h3 style="margin-top: var(--space-5);">Bonus mage : capacite de rang 2 *</h3>
        <p class="muted">Les mages obtiennent une capacite de rang 2 supplementaire a la creation. Choisissez dans quelle voie :</p>
        <div class="voie-grid">
          ${[
            ...state.voiesChoisies,
            ...(state.useVoieMage ? ['voie-du-mage'] : []),
            ...(state.voiePeupleId && !state.useVoieMage ? [state.voiePeupleId] : []),
          ].map((vid) => {
            const v = idx.voieClasseById.get(vid) ?? idx.voiePeupleById.get(vid);
            if (!v) return nothing;
            return html`
              <button
                class="voie-card ${state.voieRang2 === vid ? 'selected' : ''}"
                @click=${() => wizardUpdate({ voieRang2: vid })}
              >
                <strong>${v.nom}</strong>
                <span class="muted voie-rang1">Rang 2 : ${v.rangs[1]?.nom ?? '?'}</span>
              </button>
            `;
          })}
        </div>
      `
          : nothing
      }
    </div>
  `;
}

function renderStepEquipement(
  state: WizardState,
  profils: readonly Profil[],
  peuples: readonly Peuple[],
  idx: Awaited<ReturnType<typeof loadIndexes>>,
) {
  const profil = profils.find((p) => p.id === state.profilId);
  const peuple = peuples.find((p) => p.id === state.peupleId);
  const equipement = profil?.equipement_depart ?? [];
  const langues = peuple?.langues ?? ['commun'];

  return html`
    <div class="wizard-equip">
      <h3>Equipement de depart</h3>
      <p class="muted">Selon votre profil (${profil?.nom ?? '?'}).</p>
      <ul class="equip-list">
        ${equipement.map((e, idx) => {
          if (e.choix && e.choix.length > 1) {
            const chosen = state.equipChoices[idx] ?? e.choix[0]?.id ?? '';
            return html`
              <li class="equip-item equip-choice">
                <select
                  class="form-input"
                  @change=${(ev: Event) => {
                    const val = (ev.target as HTMLSelectElement).value;
                    wizardUpdate({ equipChoices: { ...state.equipChoices, [idx]: val } });
                  }}
                >
                  ${e.choix.map((c) => html`<option value=${c.id} ?selected=${chosen === c.id}>${c.nom}</option>`)}
                </select>
              </li>
            `;
          }
          return html`
            <li class="equip-item">
              <strong>${e.item}</strong>
              ${e.stats ? html`<span class="muted"> - ${e.stats}</span>` : nothing}
            </li>
          `;
        })}
      </ul>

      <h3 style="margin-top: var(--space-3);">Sac d'aventurier (inclus)</h3>
      <div class="trait-tags">
        <span class="trait-tag">Couverture</span>
        <span class="trait-tag">Torche</span>
        <span class="trait-tag">Briquet a silex</span>
        <span class="trait-tag">Outre</span>
        <span class="trait-tag">Gamelle</span>
      </div>

      <h3 style="margin-top: var(--space-5);">Langues et Talents</h3>
      <p class="muted">Langues du peuple : ${langues.join(', ')}.</p>
      <div class="trait-tags">
        ${langues.map((l) => html`<span class="trait-tag">${l}</span>`)}
      </div>
      ${
        state.caracAssignment.INT > 0
          ? (
              () => {
                const slotsTotal = state.caracAssignment.INT;
                const slotsUsed = state.languesSupp.length + state.talentsSecondaires.length;
                const slotsLeft = slotsTotal - slotsUsed;
                return html`
          <p class="muted" style="margin-top: var(--space-2);">INT ${formatSign(state.caracAssignment.INT)} : ${slotsTotal} choix (langues ou talents). Restants : ${slotsLeft}</p>
          ${
            slotsLeft > 0
              ? html`
            <div class="trait-row" style="margin-top: var(--space-1);">
              <select
                class="form-input trait-select"
                @change=${(e: Event) => {
                  const sel = e.target as HTMLSelectElement;
                  const v = sel.value;
                  if (v) wizardUpdate({ languesSupp: [...state.languesSupp, v] });
                  sel.value = '';
                }}
              >
                <option value="">-- Ajouter une langue --</option>
                ${LANGUES_DISPONIBLES.filter((l) => !langues.includes(l) && !state.languesSupp.includes(l)).map((l) => html`<option value=${l}>${l}</option>`)}
              </select>
              <select
                class="form-input trait-select"
                @change=${(e: Event) => {
                  const sel = e.target as HTMLSelectElement;
                  const v = sel.value;
                  if (v)
                    wizardUpdate({
                      talentsSecondaires: [...wizardState.get().talentsSecondaires, v],
                    });
                  sel.value = '';
                }}
              >
                <option value="">-- Ou un talent --</option>
                ${getAllTalentOptions(state, idx)
                  .filter((t) => !state.talentsSecondaires.includes(t))
                  .map((t) => html`<option value=${t}>${t}</option>`)}
              </select>
            </div>
          `
              : nothing
          }
          ${
            state.languesSupp.length > 0 || state.talentsSecondaires.length > 0
              ? html`
            <div class="trait-tags" style="margin-top: var(--space-2);">
              ${state.languesSupp.map(
                (l, i) => html`
                <span class="trait-tag">${l} <button class="trait-tag-remove" type="button" @click=${() => wizardUpdate({ languesSupp: state.languesSupp.filter((_, idx) => idx !== i) })}>x</button></span>
              `,
              )}
              ${
                state.talentsSecondaires.length > 0
                  ? html`
                ${state.talentsSecondaires.map((t, i) => html`<span class="trait-tag">Talent: ${t} <button class="trait-tag-remove" type="button" @click=${() => wizardUpdate({ talentsSecondaires: wizardState.get().talentsSecondaires.filter((_, idx) => idx !== i) })}>x</button></span>`)}
              `
                  : nothing
              }
            </div>
          `
              : nothing
          }
        `;
              }
            )()
          : nothing
      }
    </div>
  `;
}

async function handleCreate(state: WizardState): Promise<void> {
  if (!state.profilId || !state.peupleId) return;

  const peupleChoices = {
    optionsChoices:
      state.peupleModBonusChoices.length > 0
        ? state.peupleModBonusChoices
        : state.humainBonusCarac
          ? [state.humainBonusCarac]
          : [],
    malusChoices: state.peupleModMalusChoices,
  };

  const idx = await loadIndexes();

  const rangs: Record<string, number> = {};
  for (const vid of state.voiesChoisies) rangs[vid] = 1;
  if (state.voiePeupleId) rangs[state.voiePeupleId] = 1;
  if (state.useVoieMage) rangs['voie-du-mage'] = 1;
  if (state.voieRang2 && rangs[state.voieRang2]) rangs[state.voieRang2] = 2;
  if (state.diviniteId) {
    const divinites =
      (
        idx.bdd.pantheon as {
          divinites?: { id: string; capacite_divine: { voie_source: string; rang: number } }[];
        }
      )?.divinites ?? [];
    const div = divinites.find((d) => d.id === state.diviniteId);
    if (div?.capacite_divine.rang === 1) {
      rangs[div.capacite_divine.voie_source] = Math.max(
        rangs[div.capacite_divine.voie_source] ?? 0,
        1,
      );
    }
  }

  const finalCaracs = { ...state.caracAssignment };
  const peupleData2 = (await loadIndexes()).bdd.peuples.peuples.find(
    (pp) => pp.id === state.peupleId,
  );
  const bonusList = peupleData2?.modificateurs_carac.bonus ?? [];
  const malusList = peupleData2?.modificateurs_carac.malus ?? [];
  bonusList.forEach((b, i) => {
    if (b.carac === 'libre') {
      if (state.humainBonusCarac && state.humainBonusCarac in finalCaracs) {
        finalCaracs[state.humainBonusCarac] += Number(b.valeur);
      }
      return;
    }
    const chosen = (state.peupleModBonusChoices[i] ?? b.carac) as Carac;
    if (chosen in finalCaracs) finalCaracs[chosen] += Number(b.valeur);
  });
  malusList.forEach((m, i) => {
    const chosen = (state.peupleModMalusChoices[i] ?? m.carac) as Carac;
    if (chosen in finalCaracs) finalCaracs[chosen] += Number(m.valeur);
  });

  const input: PersonnageCreationInput = {
    nom: state.nom.trim(),
    profilId: state.isHybride ? (state.profilPrimaireId ?? state.profilId) : state.profilId,
    peupleId: state.peupleId,
    peupleChoices: {
      optionsChoices: peupleChoices.optionsChoices as string[],
      malusChoices: peupleChoices.malusChoices as string[],
    },
    caracs: finalCaracs,
    voiesProfil: state.voiesChoisies,
    voiePeupleId: state.voiePeupleId,
    useVoieMage: state.useVoieMage,
    rangs,
    concept: state.concept,
    ideal: state.ideaux.join(', '),
    defaut: state.defauts.join(', '),
    phobie: state.phobies.join(', '),
  };

  const personnage = createPersonnage(input);
  const peupleData = idx.bdd.peuples.peuples.find((p) => p.id === state.peupleId);
  const profilData = idx.bdd.profils.profils.find((p) => p.id === state.profilId);
  const langues = [...(peupleData?.langues ?? ['commun']), ...state.languesSupp];

  const startingInventory = [
    { uid: crypto.randomUUID(), cat: 'materiel', nom: 'Couverture', qty: 1 },
    { uid: crypto.randomUUID(), cat: 'materiel', nom: 'Torche', qty: 1 },
    { uid: crypto.randomUUID(), cat: 'materiel', nom: 'Briquet a silex', qty: 1 },
    { uid: crypto.randomUUID(), cat: 'materiel', nom: 'Outre', qty: 1 },
    { uid: crypto.randomUUID(), cat: 'materiel', nom: 'Gamelle', qty: 1 },
  ];

  const bddEquipNames = new Set([
    ...(idx.bdd.equipement.armures ?? []).map((a) => a.nom),
    ...(idx.bdd.equipement.boucliers ?? []).map((b) => b.nom),
    ...(idx.bdd.equipement.armes_contact ?? []).map((w) => w.nom),
    ...(idx.bdd.equipement.armes_distance ?? []).map((w) => w.nom),
    ...(idx.bdd.equipement.accessoires ?? []).map((a) => a.nom),
  ]);

  const equipDepart = profilData?.equipement_depart ?? [];
  for (const e of equipDepart) {
    const choixIdx = equipDepart.indexOf(e);
    let itemNom: string;
    if (e.choix && e.choix.length > 1) {
      const raw =
        e.choix.find((c) => c.id === state.equipChoices[choixIdx])?.nom ??
        e.choix[0]?.nom ??
        e.item;
      itemNom = raw.replace(/\s*\(.*\)/, '');
    } else {
      itemNom = e.item;
    }
    const isEquip = bddEquipNames.has(itemNom);
    const qty = e.quantite ?? 1;
    startingInventory.push({
      uid: crypto.randomUUID(),
      cat: isEquip ? 'equipement' : 'materiel',
      nom: itemNom,
      qty,
    });
  }

  if (state.diviniteId) {
    const divData = (
      idx.bdd.pantheon as { divinites?: { id: string; arme_sacree: string | string[] }[] }
    )?.divinites?.find((d) => d.id === state.diviniteId);
    if (divData?.arme_sacree) {
      const armeName = Array.isArray(divData.arme_sacree)
        ? divData.arme_sacree[0]
        : divData.arme_sacree;
      if (armeName) {
        const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1).replace(/-/g, ' ');
        startingInventory.push({
          uid: crypto.randomUUID(),
          cat: 'equipement',
          nom: capitalize(armeName),
          qty: 1,
        });
      }
    }
  }

  const bourseRoll = Math.floor(Math.random() * 6) + Math.floor(Math.random() * 6) + 2;

  const equipement = {
    armure: null as string | null,
    bouclier: null as string | null,
    main_1: null as string | null,
    main_2: null as string | null,
    distance: null as string | null,
    grimoire: null as string | null,
  };
  for (const item of startingInventory) {
    if (item.cat !== 'equipement') continue;
    const lower = item.nom.toLowerCase();
    if (
      !equipement.armure &&
      (lower.includes('armure') ||
        lower.includes('cuir') ||
        lower.includes('mailles') ||
        lower.includes('robe'))
    ) {
      equipement.armure = item.nom;
    } else if (!equipement.bouclier && lower.includes('bouclier')) {
      equipement.bouclier = item.nom;
    } else if (!equipement.grimoire && lower.includes('grimoire')) {
      equipement.grimoire = item.nom;
    } else if (
      !equipement.distance &&
      (lower.includes('arc') ||
        lower.includes('arbalete') ||
        lower.includes('fronde') ||
        lower.includes('petoire'))
    ) {
      equipement.distance = item.nom;
    } else if (
      !equipement.main_1 &&
      !lower.includes('javelot') &&
      !lower.includes('dague') &&
      item.cat === 'equipement' &&
      !equipement.armure?.includes(item.nom) &&
      !equipement.bouclier?.includes(item.nom) &&
      !equipement.distance?.includes(item.nom)
    ) {
      equipement.main_1 = item.nom;
    }
  }

  const withExtras = {
    ...personnage,
    langues,
    inventaire: startingInventory,
    equipement,
    bourse: { pp: 0, po: 0, pa: bourseRoll, pc: 0 },
    secretIntime: state.secretsIntimes.join(', '),
    talentSecondaire: state.talentsSecondaires.join(', ') || null,
    compagnons: detectCompagnons(rangs, idx),
    choixVoies: state.choixVoies,
    diviniteId: state.diviniteId,
    profilSecondaireId: state.isHybride ? state.profilSecondaireId : null,
  };
  await savePersonnage(withExtras);
  wizardReset();
  navigate('personnage-fiche', { id: withExtras.id });
}

function detectCompagnons(
  rangs: Record<string, number>,
  idx: Awaited<ReturnType<typeof loadIndexes>>,
): Array<{
  id: string;
  nom: string;
  compagnonBaseId: string;
  voieSourceId: string;
  pvActuel: null;
  etats: [];
  buffs: [];
  notes: string;
}> {
  const compagnons: Array<{
    id: string;
    nom: string;
    compagnonBaseId: string;
    voieSourceId: string;
    pvActuel: null;
    etats: [];
    buffs: [];
    notes: string;
  }> = [];
  for (const [voieId, rang] of Object.entries(rangs)) {
    const voie = idx.voieClasseById.get(voieId) ?? idx.voiePeupleById.get(voieId);
    if (!voie) continue;
    for (let r = 0; r < rang; r++) {
      const rankData = voie.rangs[r];
      if (!rankData?.effets) continue;
      for (const e of rankData.effets) {
        if (e.type === 'invocation' && e.creature_id && e.controle === 'total') {
          compagnons.push({
            id: crypto.randomUUID(),
            nom: e.creature_id.replace(/-/g, ' ').replace(/^\w/, (c: string) => c.toUpperCase()),
            compagnonBaseId: e.creature_id,
            voieSourceId: voieId,
            pvActuel: null,
            etats: [],
            buffs: [],
            notes: '',
          });
        }
      }
    }
  }
  return compagnons;
}

function syncTextInputs(): void {
  const nomEl = document.getElementById('wizard-nom') as HTMLInputElement | null;
  const conceptEl = document.getElementById('wizard-concept') as HTMLInputElement | null;
  if (nomEl) wizardUpdate({ nom: nomEl.value });
  if (conceptEl) wizardUpdate({ concept: conceptEl.value });
}

function handleNext(): void {
  syncTextInputs();
  const state = wizardState.get();
  if (isStepValid(state, state.step)) {
    wizardNext();
  }
}

function renderNav(state: WizardState, idx: Awaited<ReturnType<typeof loadIndexes>>) {
  let canNext = state.step === 1 ? true : isStepValid(state, state.step);
  if (state.step === 6) {
    const profil = idx.bdd.profils.profils.find((p) => p.id === state.profilId);
    if (profil?.famille_id === 'mages' && state.voieRang2 === null) {
      canNext = false;
    }
    for (const vid of state.voiesChoisies) {
      if (CHOIX_VOIES[vid] && !state.choixVoies[vid]) {
        canNext = false;
      }
    }
  }
  return html`
    <div class="wizard-nav">
      ${
        state.step > 1
          ? html`<button class="btn btn-secondary" @click=${wizardPrev}>Precedent</button>`
          : html`<span></span>`
      }
      ${
        state.step < 7
          ? html`<button class="btn btn-primary" ?disabled=${!canNext} @click=${handleNext}>Suivant</button>`
          : html`<button class="btn btn-primary btn-create" ?disabled=${!canNext} @click=${() => handleCreate(wizardState.get())}>Creer le personnage</button>`
      }
    </div>
  `;
}
